import { FC, InputHTMLAttributes, forwardRef, useState } from 'react';
import classNames from 'classnames';
import cls from './Input.module.scss';

type InputTheme = 'outline' | 'outlineInverted';
type InputSize = 'size_m' | 'size_l' | 'size_xl';

type HTMLInputProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'size' | 'readOnly'
>;

interface InputProps extends HTMLInputProps {
    className?: string;
    type?: string;
    label?: string;
    classLabel?: string;
    theme?: InputTheme;
    size?: InputSize;
    value?: string | number;
    readonly?: boolean;
    onlyIntegerNumber?: boolean;
    onCustomChange?: (value: string) => void;
}

export const Input: FC<InputProps> = forwardRef(
    (props, ref: React.ForwardedRef<HTMLInputElement | null>) => {
        const {
            className,
            type = 'text',
            theme = 'outline',
            size = 'size_m',
            readonly,
            value,
            onlyIntegerNumber,
            onCustomChange,
            label,
            classLabel,
            ...otherProps
        } = props;

        const [typeInput, setTypeInput] = useState(type);
        const [isVisiblePassword, setIsVisiblePassword] = useState(false);

        // проверяет что в инпут вводиться только число
        const checkForIntegerNumber = (
            e: React.KeyboardEvent<HTMLInputElement>
        ) => {
            if (!/[0-9(Backspace)(Arrow)(Delete)]/.test(e.key)) {
                e.preventDefault();
            }
        };

        const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) =>
            onlyIntegerNumber ? checkForIntegerNumber(e) : undefined;

        const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
            onCustomChange?.(e.target.value);
        };

        const onChangeType = () => {
            setIsVisiblePassword((prev) => !prev);
            setTypeInput(isVisiblePassword ? 'password' : 'text');
        };

        return (
            <label className={cls.label}>
                <input
                    ref={ref}
                    type={typeInput}
                    readOnly={readonly}
                    value={value}
                    onKeyDown={onKeyDownHandler}
                    onChange={onCustomChange ? onChangeHandler : undefined}
                    className={classNames(
                        cls.Input,
                        { [cls.readonly]: readonly},
                        {[cls.password]: type === 'password'},
                        className,
                        cls[theme],
                        cls[size]
                    )}
                    {...otherProps}
                />
                <span className={classNames(cls.labelText, classLabel)}>{label}</span>
                {type === 'password' && (
                    <span
                        onClick={onChangeType}
                        className={`${cls.iconPassword} ${
                            isVisiblePassword ? cls.eyeSlash : cls.eye
                        }`}
                    />
                )}
            </label>
        );
    }
);
